import { FileDto } from '../../models-sparta';
import { ForemanCustomerEquipment } from './foreman-customer-equipment.model';

export interface ForemanSubmitTime {
  foremanJob: ForemanSaveJob;
  foremanCustomerEquipments?: ForemanCustomerEquipment[];
  workOrders: ForemanSavePhase[];
  foremanCrews: ForemanSaveJobCrew[];
  employees: ForemanSaveEmployeeJob[];
  equipment: ForemanSaveEquipmentDispatch[];
  tasks: ForemanSaveTask[];
  materialExpendables: ForemanSaveMaterialExpendable[];
  safetyItems: ForemanSaveDailySafetyItem[];
  timeSheet: ForemanSaveTimeSheet;
  /**
   * Per-job Job Owner default (#30008). Omit entirely to leave the stored owner
   * untouched; send `{ hrRef: null }` to clear it.
   */
  jobOwner?: ForemanSaveJobOwner;
  /** Omitted on follow-up individual multi-job posts after the PDF row exists (same `timeSheet.fileID`). */
  pdfFile?: FileDto | null;
}

export interface ForemanSaveJobOwner {
  hrRef: number | null;
}

export interface ForemanSaveJob {
  foremanJobID: string;
  jobID: string | null;
  vpJobID?: string;
  companyID?: string | null;
  description?: string;
  foremanCustomerEquipmentID?: string | null;
  foremanJobStatusName?: string | null;
  foremanJobStatusNote?: string;
  bActive?: boolean;
}

export interface ForemanSaveTimeSheet {
  foremanTimeSheetID: string;
  foremanJobID: string;
  workDate: Date;
  shift: number | null;
  foremanJobCrewID: string | null;
  customerFirstName: string | null;
  customerLastName: string | null;
  customerEmail: string | null;
  exceptions?: string | null;
  fileID: string | null;
}

export interface ForemanSavePhase {
  foremanPhaseID: string;
  foremanJobID: string;
  phaseID: string | null;
  phaseName: string;
  bActive?: boolean | null;
}

export interface ForemanSaveJobCrew {
  foremanJobCrewID: string;
  foremanJobID: string;
  jobCrewID: string | null;
  name: string;
  shift: number | null;
  bActive?: boolean | null;
}

export interface ForemanSaveEmployeeJob {
  foremanEmployeeJobID: string;
  foremanJobID: string;
  employeeJobID?: string | null;
  manPowerReqDetailID?: string | null;
  foremanEmployeeID?: string | null;
  bActive?: boolean | null;
  startDate?: Date | null;
  endDate?: Date | null;
  rofDate?: Date | null;
  lastDayOnSite?: Date | null;
  shift?: number | null;
  craft?: string;
  dispatchStatusID?: number | null;
  foremanPhaseID?: string | null;
  employee?: ForemanSaveEmployee;
  employeeLabors: ForemanSaveEmployeeLabor[];
  foremanJobCrews?: ForemanSaveCrewAssignment[];
}

export interface ForemanSaveEmployee {
  foremanEmployeeID: string;
  employeeID: string | null;
  firstName: string;
  lastName: string;
  middleName: string;
  craft: string;
  companyID: string | null;
  cellPhone: string;
  last4SSN: string;
  bActive: boolean | null;
}

export interface ForemanSaveEmployeeLabor {
  foremanEmployeeLaborID: string;
  foremanEmployeeJobID: string;
  dateWorked: string;
  hours: number | null;
  foremanEarnCode: string;
  foremanPhaseID: string | null;
  bActive: boolean | null;
  shift: number | null;
  foremanJobCrewID: string | null;
  craft?: string;
  foremanNotes: string;
  isException?: boolean;
  foremanExceptionNotes?: string;
  isAbsent: boolean;
  absentReasonID: string | null;
  absentReasonOther: string;
  employeeInOuts?: ForemanSaveEmployeeInOut[];
}

export interface ForemanSaveEmployeeInOut {
  foremanEmployeeInOutID: string;
  foremanEmployeeLaborID: string;
  foremanEmployeeJobID: string;
  timeIn: Date;
  timeOut: Date;
  bActive: boolean | null;
}

export interface ForemanSaveEquipmentDispatch {
  foremanEquipmentDispatchID: string;
  equipmentDispatchID: string | null;
  foremanEquipmentID: string | null;
  foremanJobID: string;
  startDate: string | null;
  endDate: string | null;
  lastDayOnSite?: string | null;
  bActive: boolean | null;
  note: string;
  equipmentNameOverride: string;
  altID: string;
  customerID: string;
  equipmentRequestDetailID: string | null;
  foremanPhaseID: string | null;
  shift: number | null;
  equipment: ForemanSaveEquipment;
  equipmentUsages: ForemanSaveEquipmentUsage[];
  foremanJobCrews?: ForemanSaveCrewAssignment[];
}

export interface ForemanSaveEquipment {
  foremanEquipmentID: string;
  equipmentID: string | null;
  unitID: string | null;
  name: string;
  altID: string | null;
  description: string;
  foremanNotes: string | null;
  bActive: boolean | null;
}

export interface ForemanSaveEquipmentUsage {
  foremanEquipmentUsageID: string;
  dateUsed: string;
  foremanPhaseID: string | null;
  foremanEquipmentDispatchID: string;
  unitID: string | null;
  quantity: number | null;
  amount: number | null;
  bActive: boolean | null;
  shift: number | null;
  foremanJobCrewID: string | null;
  foremanNotes: string;
  notUsed: boolean;
}

export interface ForemanSaveCrewAssignment {
  foremanJobCrewID: string;
  bActive: boolean;
}

export interface ForemanSaveTask {
  foremanDailyTaskID: string;
  foremanTaskID: string;
  foremanJobID: string;
  dateWorked: string;
  shift: number | null;
  foremanJobCrewID: string | null;
  taskOrder: number;
  taskDescription: string;
  bActive: boolean | null;
  isFavorite: boolean | null;
  isNextShiftTask: boolean | null;
}

export interface ForemanSaveMaterialExpendable {
  foremanMaterialExpendableID: string;
  foremanJobID: string;
  dateWorked: string;
  shift: number | null;
  foremanJobCrewID: string | null;
  description: string;
  quantity: number;
  totalPrice: number;
  bActive: boolean | null;
}

export interface ForemanSaveDailySafetyItem {
  foremanDailySafetyItemID: string;
  foremanSafetyItemID: string;
  foremanJobID: string;
  dateWorked: string;
  isItemCompleted: boolean;
}
